import CookieConsent from '@/components/CookieConsent';
import { PostHogInitializer } from '@/components/analytics/PostHogInitializer';
import { StateContext } from '@/context/StateContext';
import { useSessionTimeout } from '@/hooks/useSessionTimeout';
import validateStripeConfigAndLog from '@/utils/stripe-config-validator';
import { Analytics } from '@vercel/analytics/react';
import Cookies from 'js-cookie';
import { SessionProvider, useSession } from 'next-auth/react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import Script from 'next/script';
import { useEffect, useState } from 'react';
import { Toaster } from 'react-hot-toast';
import SubscriptionEnvironmentInitializer from '../src/components/SubscriptionEnvironmentInitializer';
import Footer from '../src/components/common/Footer';
import FooterSimpleDark from '../src/components/common/FooterSimpleDark';
import FooterSmall from '../src/components/common/FooterSmall';
import HeaderWrapper from '../src/components/common/HeaderWrapper';
import AccountLayout from '../src/components/layout/AccountLayout';
import '../styles/main.scss';
import '../styles/normalize.css';
import './styles/landing-vmc.scss';

// Run Stripe configuration validation at server startup
if (typeof window === 'undefined') {
  try {
    console.log('Server-side startup: Validating Stripe configuration');
    validateStripeConfigAndLog();
  } catch (error) {
    console.error('Error validating Stripe configuration:', error);
  }
}

// Create a new component to handle session timeout
function SessionTimeoutWrapper({ children }) {
  useSessionTimeout();
  return children;
}

// Component to handle layout based on route (needs session access)
function AppContent({
  Component,
  pageProps,
  pathname,
  isAccountPage,
  isHeaderHidden,
  isFooterHidden,
  isEnrollmentPage,
}) {
  const { data: session } = useSession();

  // Extract layout config from page component (if it exists)
  const layoutConfig = Component.layoutConfig || {};

  // Account pages use AccountLayout (sidebar + content)
  if (isAccountPage) {
    return (
      <>
        <SubscriptionEnvironmentInitializer />
        <AccountLayout session={session} config={layoutConfig}>
          <Component {...pageProps} />
        </AccountLayout>
        <Analytics />
        <FooterSmall />
      </>
    );
  }

  // Marketing pages use HeaderWrapper + standard layout
  return (
    <>
      <SubscriptionEnvironmentInitializer />
      {!isHeaderHidden && <HeaderWrapper />}
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          flex: '1 0 auto',
          paddingTop: 0,
        }}
      >
        <Component {...pageProps} />
      </div>
      <Analytics />
      {!isFooterHidden && !isHeaderHidden ? (
        isEnrollmentPage ? (
          <FooterSmall />
        ) : (
          <Footer />
        )
      ) : isFooterHidden ? (
        <FooterSimpleDark />
      ) : (
        <FooterSmall />
      )}
    </>
  );
}

function MyApp({ Component, pageProps: { session, ...pageProps } }) {
  const [cookieConsent, setCookieConsent] = useState(null);
  const router = useRouter();
  const pathname = router.pathname;

  useEffect(() => {
    // Read the cookie on mount (only runs on client)
    const consent = Cookies.get('cookie_consent');
    setCookieConsent(consent);
  }, []);

  // Define path-based layout logic (extracted from App Router)
  const pathsWithoutHeader = [
    '/auth/signup',
    '/auth/partner-signup',
    '/auth/signin',
    '/auth/verify',
    '/auth/check-email',
    '/forgot-password',
    '/auth/reset-password',
    '/privacy-policy',
    '/terms',
    '/refund-policy',
    '/checkout',
  ];

  const pathsWithoutHeaderFooter = [
    '/landing/digicert-verified-mark-certificate',
    '/landing/digicert-common-mark-certificate',
    '/landing/digicert-government-mark-certificate',
    '/landing/entrust-to-digicert-vmc',
    '/checkout',
  ];

  const isHeaderHidden =
    pathsWithoutHeader.includes(pathname) ||
    pathsWithoutHeaderFooter.includes(pathname) ||
    pathname?.startsWith('/landing/entrust-to-digicert/') ||
    pathname?.startsWith('/renew-vmc');
  const isFooterHidden =
    pathsWithoutHeaderFooter.includes(pathname) ||
    pathname?.startsWith('/landing/entrust-to-digicert/') ||
    pathname?.startsWith('/renew-vmc');
  const isAccountPage = pathname?.startsWith('/account'); // Removed trailing slash to match both /account and /account/*
  const isEnrollmentPage = pathname?.startsWith('/account/digicert-enrollment');

  return (
    <>
      {cookieConsent === 'accepted' && (
        <>
          <Script
            strategy="afterInteractive"
            src="https://www.googletagmanager.com/gtag/js?id=G-PHH0JLNSMW"
          />
          <Script
            id="gtag-init"
            strategy="afterInteractive"
            dangerouslySetInnerHTML={{
              __html: `
                window.dataLayer = window.dataLayer || [];
                function gtag(){dataLayer.push(arguments);}
                gtag('js', new Date());
                gtag('config', 'G-PHH0JLNSMW');
              `,
            }}
          />
        </>
      )}

      <Head>
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width, initial-scale=1" />
        <link
          rel="icon"
          href="/images/favicon.ico"
          sizes="48x48 96x96 144x144 256x256"
          type="image/x-icon"
        />
        <link rel="icon" href="/images/favicon-192.png" sizes="192x192" type="image/png" />
        <link rel="apple-touch-icon" href="/images/apple-touch-192.png" />
        <link rel="icon" href="/images/favicon-512.png" sizes="512x512" type="image/png" />
      </Head>
      <SessionProvider session={session}>
        <PostHogInitializer hasConsent={cookieConsent === 'accepted'} />
        <StateContext>
          <SessionTimeoutWrapper>
            <div
              style={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
              }}
            >
              <div className="toast-container">
                <Toaster
                  toastOptions={{
                    className: '',
                    duration: 5000,
                    style: {
                      background: '#363636',
                      color: '#fff',
                    },
                  }}
                />
              </div>
              <AppContent
                Component={Component}
                pageProps={pageProps}
                pathname={pathname}
                isAccountPage={isAccountPage}
                isHeaderHidden={isHeaderHidden}
                isFooterHidden={isFooterHidden}
                isEnrollmentPage={isEnrollmentPage}
              />
            </div>
          </SessionTimeoutWrapper>
        </StateContext>
      </SessionProvider>
      <CookieConsent onConsentChange={(value) => setCookieConsent(value)} />
    </>
  );
}

export default MyApp;
